/* eslint-disable no-alert */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Container from 'components/common/Container'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import Button from 'components/common/Button'
import ConceptImageInput from 'components/ConceptImageInput'
import PhotosCard from 'components/PhotosCard'
import ConfirmModal from 'components/modals/ConfirmModal'
import DeleteButton from 'components/DeleteButton'
import useFetchConcept from 'hooks/query/useFetchConcept'
import { deleteConceptItems, editConcept, postItemsForConcept } from 'api'

import c from './AlbumItem.module.scss'

function AlbumItem() {
  const [checkedAssets, setCheckedAssets] = useState<{
    [key: number]: boolean
  }>({})
  const [selectedToDelete, setSelectedToDelete] = useState<number[]>([])
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false)
  const [imageFiles, setImageFiles] = useState<(File | undefined)[]>([])
  const [isDeleting, setIsDeleting] = useState(false)
  const [albumName, setAlbumName] = useState('')

  const { t } = useTranslation()
  const { albumId } = useParams()
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const queryClient = useQueryClient()

  const {
    data: concept,
    refetch,
    isLoading,
  } = useFetchConcept({
    conceptId: Number(albumId),
  })

  const mutationPostItemsToConcept = useMutation<void, Error>(() => {
    const validImageFiles = imageFiles.filter(
      (file): file is File => file !== undefined,
    )
    return postItemsForConcept(Number(albumId), validImageFiles)
  })

  const mutationConceptItemsDelete = useMutation<void, Error>(() => {
    return deleteConceptItems(Number(albumId), selectedToDelete)
  })

  const mutationEdit = useMutation<void, Error>(() => {
    return editConcept(Number(albumId), albumName)
  })

  const handleDeleteToggle = useCallback(() => {
    setCheckedAssets({})
    setIsDeleting((prevIsDeleting) => !prevIsDeleting)
    setSelectedToDelete([])
  }, [])

  const handleMassRemove = () => {
    setImageFiles((prevFiles) => {
      const keysArray = Object.keys(checkedAssets).map(Number)
      const newFiles = prevFiles.filter(
        (_, index) => !keysArray.includes(index),
      )
      return newFiles.length > 0 ? newFiles : []
    })
  }

  const handlePostItems = async () => {
    const result = window.confirm('Do you really want to add these photos?')
    if (result) {
      await mutationPostItemsToConcept.mutateAsync()
      await refetch()
      setImageFiles([])
      toast.success('Everything went well')
    }
  }

  const selectedItemsToDelete = () => {
    mutationConceptItemsDelete.mutateAsync().then(() => {
      queryClient.invalidateQueries([`concept`, albumId])
      queryClient.refetchQueries(['infinite-concepts'])
      refetch()
      toast.success('Everything went well')
      handleMassRemove()
    })
  }

  const handleMassDelete = () => {
    if (isDeleting) {
      if (selectedToDelete.length === 0) {
        setIsDeleting(false)
        return
      }
      selectedItemsToDelete()
      setCheckedAssets({})
      setIsDeleting(false)
      setSelectedToDelete([])
      setIsConfirmModalOpen(false)
    }
  }

  const selectToDelete = useCallback(
    (fileId: number) => {
      if (selectedToDelete.includes(fileId)) {
        setSelectedToDelete(selectedToDelete.filter((elem) => elem !== fileId))
      } else {
        setSelectedToDelete([...selectedToDelete, fileId])
      }
    },
    [selectedToDelete],
  )

  const onImageChange = useCallback((e: any) => {
    const { files } = e.target
    if (files.length > 0) {
      setImageFiles((prevFiles) => {
        return [...prevFiles, ...files]
      })
    }
  }, [])

  useEffect(() => {
    if (imageFiles.length > 0) {
      handlePostItems()
    }
  }, [imageFiles])

  const closeModal = useCallback(() => {
    setIsConfirmModalOpen(false)
  }, [])

  const handleDeleteItems = useCallback(() => {
    setIsConfirmModalOpen(true)
    setIsDeleting(true)
  }, [])

  const handleCheckboxChange = (id: number) => {
    selectToDelete(id)
    setCheckedAssets((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const imageUrls = useMemo(() => {
    return imageFiles.map((file) => {
      if (file) return URL.createObjectURL(file)
      return undefined
    })
  }, [imageFiles])

  useEffect(() => {
    if (concept) setAlbumName(concept?.name)
  }, [concept])

  const handleMutationEdit = async () => {
    const result = window.confirm('Do you really want to edit your album name?')
    if (result) {
      mutationEdit.mutateAsync().then(() => {
        queryClient.invalidateQueries([`concept`, albumId])
        queryClient.refetchQueries(['infinite-concepts'])
      })
      toast.success('Everything went well')
    }
  }

  const handleChangeAlbumName = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setAlbumName(event.target.value)

    const textarea = textareaRef.current
    if (!textarea) return

    textarea.style.height = 'auto'
    const scHeight = event.target.scrollHeight
    textarea.style.height = `${scHeight}px`
  }

  if (isLoading) return null

  return (
    <div className={c.albumPhotos}>
      <ConfirmModal
        heading={t('modals.general.delete')}
        confirmFunc={handleMassDelete}
        cancelFunc={closeModal}
        isModalOpen={isConfirmModalOpen}
      />
      <DeleteButton
        checkedAssets={checkedAssets}
        selectedToDelete={selectedToDelete}
        isDeleting={isDeleting}
        handleDeleteToggle={handleDeleteToggle}
        handleMassDelete={handleDeleteItems}
      />
      <div className={c.albumActions}>
        <div className={c.action}>
          <textarea
            placeholder={t('profile.setAlbumName')}
            className={c.textarea}
            value={albumName}
            rows={1}
            onChange={handleChangeAlbumName}
            ref={textareaRef}
          />
          <Button
            className={c.conceptNameEdit}
            type="button"
            onClick={handleMutationEdit}
          >
            {t('profile.upgradeConcept')}
          </Button>
        </div>
      </div>
      <Container flex>
        {concept && (
          <div className={c.grid}>
            <ConceptImageInput onChange={onImageChange} />
            {concept?.file_ids &&
              concept?.file_ids.length > 0 &&
              concept.file_ids.map((elem) => (
                <PhotosCard
                  key={elem.id}
                  data={elem}
                  isChecked={checkedAssets[elem.id] || false}
                  handleCheckboxChange={handleCheckboxChange}
                  setImageFiles={setImageFiles}
                  conceptId={Number(albumId)}
                  isConceptPhoto
                />
              ))}
            {imageUrls.map((url, index) => {
              if (!url) return null
              const data = { id: index, key: '', link: url }
              return (
                <PhotosCard
                  key={url}
                  data={data}
                  isChecked={checkedAssets[index] || false}
                  handleCheckboxChange={handleCheckboxChange}
                  setImageFiles={setImageFiles}
                />
              )
            })}
          </div>
        )}
      </Container>
    </div>
  )
}

export default AlbumItem
